import { useTranslation } from "react-i18next";
import { Users, Trash2, ChevronRight, MapPin } from "lucide-react";
import { Card, Badge } from "@tabipla/ui";
import type { GroupResponseDto } from "@tabipla/shared";

interface GroupCardProps {
  group: GroupResponseDto;
  onClick: () => void;
  onDelete?: (e: React.MouseEvent) => void;
}

export function GroupCard({ group, onClick, onDelete }: GroupCardProps) {
  const { t } = useTranslation();

  const getRoleBadgeStyle = (role: string) => {
    switch (role) {
      case "owner":
        return "bg-primary/10 text-primary";
      case "editor":
        return "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300";
      default:
        return "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";
    }
  };

  // Determine if we have a cover image
  const hasCoverImage = !!group.coverImageUrl;

  // Render the icon (emoji or image)
  const renderIcon = () => {
    if (group.iconImageUrl) {
      return (
        <img
          src={group.iconImageUrl}
          alt=""
          className="h-12 w-12 rounded-full object-cover ring-2 ring-background"
        />
      );
    }

    const iconColor = group.iconColor || "from-primary/60 to-primary/40";
    const iconEmoji = group.iconEmoji || group.name.charAt(0).toUpperCase();

    return (
      <div
        className={`flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br ${iconColor} text-2xl ring-2 ring-background`}
      >
        {iconEmoji}
      </div>
    );
  };

  return (
    <Card interactive className="overflow-hidden" onClick={onClick}>
      {/* Cover image or gradient background */}
      {hasCoverImage ? (
        <div className="relative">
          <img src={group.coverImageUrl!} alt="" className="h-24 w-full object-cover" />
          {/* Gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

          {/* Floating icon */}
          <div className="absolute bottom-0 left-4 translate-y-1/2">{renderIcon()}</div>

          {/* Delete button on cover */}
          {onDelete && group.myRole === "owner" && (
            <button
              onClick={onDelete}
              className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/30 text-white backdrop-blur-sm transition-colors hover:bg-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      ) : (
        // No cover - gradient header with icon
        <div className="relative h-16 bg-gradient-to-br from-primary/20 to-primary/5">
          {/* Icon positioned at bottom-left */}
          <div className="absolute bottom-0 left-4 translate-y-1/2">{renderIcon()}</div>

          {/* Delete button */}
          {onDelete && group.myRole === "owner" && (
            <button
              onClick={onDelete}
              className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {/* Content area */}
      <div className={`flex flex-col p-4 ${hasCoverImage ? "pt-8" : "pt-8"}`}>
        {/* Header: Name + Arrow */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h3 className="line-clamp-1 text-lg font-semibold">{group.name}</h3>
            {group.description && (
              <p className="mt-0.5 line-clamp-1 text-sm text-muted-foreground">
                {group.description}
              </p>
            )}
          </div>
          <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-muted-foreground" />
        </div>

        {/* Stats + Role */}
        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
          <span className="flex items-center gap-1 text-muted-foreground">
            <Users className="h-3.5 w-3.5" />
            {t("groups.memberCount", "{{count}}人", { count: group.memberCount })}
          </span>
          <span className="flex items-center gap-1 text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" />
            {t("groups.tripCount", "{{count}}旅行", { count: group.tripCount })}
          </span>
          <Badge className={getRoleBadgeStyle(group.myRole)}>
            {t(`groups.roles.${group.myRole}`)}
          </Badge>
        </div>
      </div>
    </Card>
  );
}
